import type { VanillaLinkProps } from '@_/ui';
import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppShell } from '../useAppShell.ts';

export function DesktopNavItem({
  icon,
  label,
  onClick,
  renderLink,
  renderPopover,
  subtle = false
}: {
  icon?: React.ReactNode;
  label?: React.ReactNode;
  onClick?: () => void;
  renderLink?: (
    props: Pick<VanillaLinkProps, 'children' | 'className' | 'onClick' | 'plain'>
  ) => React.ReactNode;
  renderPopover?: NavPopoverProps['children'];
  subtle?: boolean;
}) {
  const classes = clsx(
    'inline-flex items-center rounded-full text-sm font-medium text-gray-800',
    subtle
      ? 'bg-black/5 group-hover:bg-black/7.5 hover:bg-black/7.5'
      : 'group-hover:bg-black/5 hover:bg-black/5',
    icon && !label ? 'px-2.25 py-2.25' : 'px-2 py-2'
  );

  const buttonContent = (
    <>
      {!!icon && (
        <span className="mr-0.5 flex items-center justify-center text-gray-700 *:size-4.5">
          {icon}
        </span>
      )}

      {!!label && <span className="px-1">{label}</span>}
    </>
  );

  const button = renderLink ? (
    renderLink({
      children: buttonContent,
      className: classes,
      onClick,
      plain: true
    })
  ) : (
    <button className={classes} onClick={onClick}>
      {buttonContent}
    </button>
  );

  if (renderPopover) {
    return (
      <div className="group">
        {button}
        <NavPopover>{renderPopover}</NavPopover>
      </div>
    );
  }

  return button;
}

interface NavPopoverProps {
  children?: (data: { onClose: () => void }) => React.ReactNode;
}

function NavPopover({ children }: NavPopoverProps) {
  const { appShellBounds } = useAppShell();

  const [forceHide, setForceHide] = useState(false);
  const forceHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleClosePopover = useCallback(() => {
    setForceHide(true);
    forceHideTimeoutRef.current = setTimeout(() => {
      forceHideTimeoutRef.current = null;
      setForceHide(false);
    }, 100);
  }, []);

  useEffect(
    () => () => {
      if (forceHideTimeoutRef.current) {
        clearTimeout(forceHideTimeoutRef.current);
      }
    },
    []
  );

  return (
    <div
      className={clsx(
        'absolute top-10 right-6 left-6 z-[9] pt-4',
        'pointer-events-none invisible translate-y-1 opacity-0',
        !forceHide &&
          'transition-all group-hover:pointer-events-auto group-hover:visible group-hover:translate-y-0 group-hover:opacity-100'
      )}
    >
      <div
        className="overflow-y-auto rounded-md bg-white shadow-lg ring-1 ring-black/5"
        style={{
          maxHeight: `calc(100vh - ${appShellBounds.top}px - 2rem)`
        }}
      >
        {children ? children({ onClose: handleClosePopover }) : null}
      </div>
    </div>
  );
}
